/**
 * Sponsor profile update - pure state transition logic.
 * 
 * Elm-ish architecture: Update is a pure function (no side effects).
 * Given a model and a message, returns a new model.
 * 
 * Profile activation per REQ-125, REQ-126, REQ-127:
 * - Gender-first flow enforced: goal disabled until gender selected
 * - Age validation: 18-120
 */
import { MIN_AGE, MAX_AGE } from '@/api/profiles';
import type { SponsorProfileModel } from './model';
import type { SponsorProfileMsg } from './messages';
import { canSelectGoal, profileToForm } from './model';

/**
 * Effect types for sponsor profile program.
 */
export type SponsorProfileEffect =
  | { readonly type: 'LoadProfile' }
  | { readonly type: 'SaveProfile'; readonly form: import('./model').ProfileFormData };

/**
 * Update the model based on a message.
 * This is a pure function - no side effects.
 * 
 * @param model - Current model state
 * @param msg - Message to process
 * @returns Updated model state
 */
export function updateSponsorProfile(
  model: SponsorProfileModel,
  msg: SponsorProfileMsg
): SponsorProfileModel {
  switch (msg.type) {
    case 'LoadSucceeded': {
      const isActive = msg.profileStatus === 'active';
      const newForm = msg.profile ? {
        gender: msg.profile.gender,
        goal: msg.profile.goal,
        age: msg.profile.age,
        display_name: msg.profile.display_name || '',
        headline: msg.profile.headline || '',
        bio: msg.profile.bio || '',
        city_or_region: msg.profile.city_or_region || '',
      } : model.form;
      
      return {
        ...model,
        status: msg.profile ? (isActive ? 'active' : 'ready') : 'empty',
        profile: msg.profile,
        form: newForm,
        error: null,
        pendingAction: null,
        profileStatus: msg.profileStatus,
        missingActivationFields: msg.missingActivationFields,
        canSelectGoal: canSelectGoal(newForm),
      };
    }
    
    case 'LoadFailed':
      return {
        ...model,
        status: 'error',
        error: msg.error,
        pendingAction: null,
      };
    
    case 'EditStarted':
      return {
        ...model,
        pendingAction: 'StartEdit',
      };
    
    case 'FormUpdated':
      return {
        ...model,
        form: msg.form,
        canSelectGoal: canSelectGoal(msg.form),
      };
    
    case 'GenderChanged': {
      // When gender changes, reset goal if gender becomes undefined
      const newForm = {
        ...model.form,
        gender: msg.gender,
        // Reset goal if gender is being cleared (per REQ-126 gender-first rule)
        goal: msg.gender === undefined ? undefined : model.form.goal,
      };
      return {
        ...model,
        form: newForm,
        canSelectGoal: canSelectGoal(newForm),
      };
    }
    
    case 'GoalChanged': {
      // Per REQ-126, goal can only be changed if gender is already set
      if (!model.canSelectGoal) {
        return model; // Goal change rejected silently if gender not selected
      }
      return {
        ...model,
        form: { ...model.form, goal: msg.goal },
      };
    }
    
    case 'AgeChanged': {
      // Validate age per REQ-125 (must be 18-120)
      let ageError = '';
      if (msg.age !== undefined) {
        if (msg.age < MIN_AGE) {
          ageError = 'You must be at least 18 years old';
        } else if (msg.age > MAX_AGE) {
          ageError = 'Age must be 120 or less';
        }
      }
      return {
        ...model,
        form: { ...model.form, age: msg.age },
        error: ageError || null,
      };
    }
    
    case 'SaveSucceeded': {
      const isActive = msg.profileStatus === 'active';
      return {
        ...model,
        status: isActive ? 'active' : 'saved',
        profile: msg.profile,
        form: {
          gender: msg.profile.gender,
          goal: msg.profile.goal,
          age: msg.profile.age,
          display_name: msg.profile.display_name || '',
          headline: msg.profile.headline || '',
          bio: msg.profile.bio || '',
          city_or_region: msg.profile.city_or_region || '',
        },
        error: null,
        pendingAction: null,
        profileStatus: msg.profileStatus,
        missingActivationFields: msg.missingActivationFields,
        canSelectGoal: canSelectGoal(profileToForm(msg.profile)),
      };
    }
    
    case 'SaveFailed':
      return {
        ...model,
        status: 'error',
        error: msg.error,
        pendingAction: null,
      };
    
    case 'EditCancelled':
      return {
        ...model,
        pendingAction: null,
        // Reset form to profile values
        form: model.profile 
          ? {
              gender: model.profile.gender,
              goal: model.profile.goal,
              age: model.profile.age,
              display_name: model.profile.display_name || '',
              headline: model.profile.headline || '',
              bio: model.profile.bio || '',
              city_or_region: model.profile.city_or_region || '',
            }
          : model.form,
      };
    
    case 'Retried':
      return {
        ...model,
        status: 'idle',
        error: null,
      };
    
    default:
      // Exhaustive match - should never reach here
      return model;
  }
}
